import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router';
import Home from '@/views/Home.vue';
import { isLoggedIn } from '@/api/auth';
import { ElMessage } from 'element-plus';
import { canAccessMenu } from '@/utils/permissions';

const routes: Array<RouteRecordRaw> = [
    {
        path: '/redirect/:path*',
        component: () => import('@/views/Redirect.vue'),
    },
    {
        path: '/',
        redirect: '/productManagement',
    },
    // 登录页面（无需认证）
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/Login.vue'),
        meta: { requiresAuth: false, keepAlive: false },
    },
    {
        path: '/index',
        name: 'Home',
        component: Home,
        meta: { requiresAuth: true, keepAlive: true },
    },
    {
        path: '/:catchAll(.*)',
        name: '404',
        component: () => import('@/views/404.vue'),
    },
    {
        path: '/productManagement',
        name: '产品管理',
        component: () => import('@/views/productManagement/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    {
        // 产品新增/编辑：含 7 个 wangEditor 实例与 SkuModal 弹窗，
        // 切走后再回来若被 keep-alive 会带着旧 formData / 残留 editor 实例，
        // 容易出 "Cannot find textarea instance by editor" 报错，因此不参与缓存
        path: '/productManagement/add',
        name: '产品编辑',
        component: () => import('@/views/productManagement/add.vue'),
        meta: { requiresAuth: true, keepAlive: false },
    },
    {
        path: '/productManagement/sku',
        name: 'SKU管理',
        component: () => import('@/views/productManagement/skuManagement.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    {
        path: '/productManagement/stockPrice',
        name: '库存价格管理',
        component: () =>
            import('@/views/productManagement/stockPriceManagement.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 订单管理模块
    {
        path: '/orderManagement',
        name: '订单管理',
        component: () => import('@/views/orderManagement/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 出行管理模块
    {
        path: '/travelManagement',
        name: '出行管理',
        component: () => import('@/views/travelManagement/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 出行管理 V2（原生 div 虚拟滚动版本，独立并存，便于 A/B 对比）
    {
        path: '/travelManagementV2',
        name: '出行管理(V2)',
        component: () => import('@/views/travelManagement/v2/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 轮播图管理模块
    {
        path: '/bannerManagement',
        name: '轮播图管理',
        component: () => import('@/views/bannerManagement/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 账号管理模块
    {
        path: '/accountManagement',
        name: '账号管理',
        component: () => import('@/views/accountManagement/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 角色管理模块
    {
        path: '/roleManagement',
        name: '角色管理',
        component: () => import('@/views/roleManagement/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 数据统计模块
    {
        path: '/dataStatistics',
        name: '数据统计',
        component: () => import('@/views/dataStatistics/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 短信模板管理模块
    {
        path: '/smsTemplate',
        name: '短信模板管理',
        component: () => import('@/views/smsTemplate/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // FAQ 常见问题管理
    {
        path: '/faqManagement',
        name: 'FAQ管理',
        component: () => import('@/views/faqManagement/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 商家转账管理模块
    {
        path: '/merchantTransfer',
        name: '商家打款管理',
        component: () => import('@/views/merchantTransfer/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 身份证黑名单管理
    {
        path: '/idCardBlacklist',
        name: '黑名单管理',
        component: () => import('@/views/idCardBlacklist/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
    // 系统设置模块
    {
        path: '/settings',
        name: '系统设置',
        component: () => import('@/views/settings/index.vue'),
        meta: { requiresAuth: true, keepAlive: true },
    },
];

const router = createRouter({
    history: createWebHashHistory(),
    routes,
});

// 路由守卫：检查登录状态 + 角色菜单权限
router.beforeEach((to, from, next) => {
    // 如果路由需要认证
    if (to.meta.requiresAuth !== false) {
        if (!isLoggedIn()) {
            // 未登录，跳转到登录页
            ElMessage.warning('请先登录');
            next({
                path: '/login',
                query: { redirect: to.fullPath }, // 保存原始路径，登录后跳转回来
            });
            return;
        }
    }

    // 如果已登录且访问登录页，跳转到首页
    if (to.path === '/login' && isLoggedIn()) {
        next('/productManagement');
        return;
    }

    // 角色菜单权限校验：菜单级路由（top-level）不允许越权访问，
    // 详情/子页面（如 /productManagement/add）跟随其归属模块。
    // 子页面路径以菜单级路径开头时视为同一模块，沿用菜单级判定。
    const topLevel = '/' + to.path.split('/').filter(Boolean)[0];
    const guardedTopLevels = [
        '/index',
        '/productManagement',
        '/orderManagement',
        '/travelManagement',
        '/travelManagementV2',
        '/bannerManagement',
        '/accountManagement',
        '/roleManagement',
        '/dataStatistics',
        '/smsTemplate',
        '/faqManagement',
        '/merchantTransfer',
        '/idCardBlacklist',
        '/settings',
    ];
    if (
        to.meta.requiresAuth !== false &&
        guardedTopLevels.includes(topLevel) &&
        !canAccessMenu(topLevel)
    ) {
        ElMessage.warning('当前角色无权访问该页面');
        next('/index');
        return;
    }

    next();
});

export default router;
